import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { emit, waitForEvent } from '../../utilities/event-legacy';
import { watch } from '../../utilities/watch-legacy';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { SlotControllerLegacy } from '../../controllers/slot-controller-legacy';
import { MobileControllerLegacy } from '../../controllers/mobile-controller-legacy';
import baseCSS from './se-dropdown.base.inner.pcss?inline';
import innerCSS from './se-dropdown.inner.pcss?inline';
import type { LinkTargetType } from '../se-link/config';
import { SeButtonVariant } from '../se-button/se-button';
import { SeMainHeader } from '../se-main-header/se-main-header';
import '../se-split-button/se-split-button';
import '../se-button/se-button';
import '../outline-icon/outline-icon';

type ButtonVariant = 'none' | 'primary' | 'secondary';

/**
 * The SeDropdown component
 * @element se-dropdown
 *
 * @slot header - Content to be rendered in the header of the dropdown.
 * @slot dropdown - Content to be rendered in the dropdown.
 * @slot footer - Content to be rendered in the footer of the dropdown.
 *
 * @event outline-show - Emitted when the dropdown opens.
 * @event outline-after-show - Emitted after the dropdown opens and all animations are complete.
 * @event outline-hide - Emitted when the dropdown closes.
 * @event outline-after-hide - Emitted after the dropdown closes and all animations are complete.
 */
@customElement('se-dropdown')
export class SeDropdown extends LitElement {
  static styles = [];

  /* Base styles first, se-dropdown overrides second — matches the sheet
   * order from the former OutlineDropdownLegacy inheritance chain. */
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: [baseCSS, innerCSS].join('\n'),
  });

  mobileControllerLegacy = new MobileControllerLegacy(this);
  slots = new SlotControllerLegacy(this, true);

  @query('.dropdown__trigger')
  trigger: HTMLElement;

  @query('.dropdown__panel')
  panel: HTMLElement;

  /**
   * Indicates whether or not the dropdown is open.
   * You can use this in lieu of the show/hide methods.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-open' })
  isOpen = false;

  /**
   * Disables the dropdown so the panel will not open.
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  /**
   * Determines if the dropdown trigger is a link or not.
   */
  @property({ type: Boolean, attribute: false })
  isLink = false;

  /**
   * The dropdown will close when the user interacts outside of this element (e.g. clicking).
   */
  @property({ attribute: false })
  containingElement?: HTMLElement;

  /**
   * The url to use for a link. This will render an anchor element.
   * Do not set this prop if you want to render a button element.
   */
  @property({ type: String, attribute: 'trigger-url' })
  triggerUrl: string;

  /**
   * Visible text for the button/link of the trigger element.
   */
  @property({ type: String, attribute: 'trigger-text' })
  triggerText: string;

  /**
   * ARIA label attribute to pass down to the resulting button or a
   * element. This is required for accessibility if we use a button
   * with an icon only.
   */
  @property({ type: String, attribute: 'trigger-label' })
  triggerLabel: string;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'trigger-target' })
  triggerTarget: LinkTargetType;

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'trigger-variant' })
  triggerVariant: ButtonVariant = 'primary';

  /**
   * The button style variant to use.
   * Declared after triggerVariant so the trigger-variant attribute
   * maps to this property, as it did in the subclass previously.
   */
  @property({ type: String, attribute: 'trigger-variant', reflect: true })
  seTriggerVariant: SeButtonVariant = 'dropdown-nav';

  @state() hasHeader: boolean;
  @state() hasDropdown: boolean;
  @state() hasFooter: boolean;

  seed: string = Math.floor(Math.random() * 100000).toString();

  connectedCallback() {
    super.connectedCallback();
    this.hasDropdown = this.slots.test('dropdown');
    this.hasHeader = this.slots.test('header');
    this.hasFooter = this.slots.test('footer');
    // @todo: Is any of this needed?
    this.handleButtonTrigger = this.handleButtonTrigger.bind(this);
    this.handleIconTrigger = this.handleIconTrigger.bind(this);
    this.handleEnterKeyDown = this.handleEnterKeyDown.bind(this);
    this.handleEscKeyDown = this.handleEscKeyDown.bind(this);
    this.handleFocusChange = this.handleFocusChange.bind(this);
    this.handleIconClick = this.handleIconClick.bind(this);

    if (!this.containingElement) {
      this.containingElement = this;
    }
  }

  updated() {
    // This check is required for "dropdown" without
    // a panel slot.
    if (this.panel) {
      this.panel.hidden = !this.isOpen;
    }
  }

  disconnectedCallback() {
    this.hide();
  }

  /**
   * Shows the dropdown panel.
   */
  async show() {
    // If the dropdown is already open, do nothing.
    // If the dropdown is disabled, do nothing.
    if (this.isOpen || this.isDisabled) {
      return undefined;
    }

    if (this.panel) {
      this.panel.hidden = false;
    }

    this.isOpen = true;
    return waitForEvent(this, 'outline-after-show');
  }

  /**
   * Hides the dropdown panel.
   */
  async hide() {
    if (!this.isOpen) {
      return undefined;
    }
    if (this.panel) {
      this.panel.hidden = true;
    }
    this.isOpen = false;
    return waitForEvent(this, 'outline-after-hide');
  }

  @watch('open', { waitUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.isDisabled) {
      this.isOpen = false;
      return;
    }
    if (this.isOpen) {
      // Show the panel.
      emit(this, 'outline-show');
      emit(this, 'outline-after-show');
    } else {
      // Hide the panel.
      emit(this, 'outline-hide');
      emit(this, 'outline-after-hide');
    }
  }

  handleDocumentMouseDown(event: MouseEvent) {
    // Close when clicking outside of the containing element
    const path = event.composedPath();
    if (this.containingElement && !path.includes(this.containingElement)) {
      this.hide();
    }
  }

  handleEscKeyDown(event: KeyboardEvent) {
    // @todo: This isn't working right now.
    // Close when escape is pressed.
    if (event.key === 'Escape') {
      this.hide();
      this.focusOnTrigger();
      return;
    }
  }

  handleFocusChange() {
    setTimeout(() => {
      const activeElement =
        this.containingElement?.getRootNode() instanceof ShadowRoot
          ? document.activeElement?.shadowRoot?.activeElement
          : document.activeElement;

      if (
        !this.containingElement ||
        activeElement?.closest(this.containingElement.tagName.toLowerCase()) !==
          this.containingElement
      ) {
        this.hide();
      }
    });
  }

  handleEnterKeyDown(event: KeyboardEvent, isIcon = false) {
    if (event.key === 'Enter') {
      if (this.isDisabled) {
        // Prevent a link from being followed.
        // Prevent the dropdown from opening.
        event.preventDefault();
        return;
      }

      if ((this.triggerUrl && isIcon) || !this.triggerUrl) {
        // This dropdown element is a link and
        // a dropdown. We should open the dropdown when
        // the icon is clicked.
        this.isOpen ? this.hide() : this.show();
        event.preventDefault();
        return;
      }

      if (this.triggerUrl) {
        // This button is a link.
        // Prevent the panel from being opened via keyboard.
        // Instead, the Enter key will navigate to the trigger url.
        return;
      }
    }
  }

  focusOnTrigger() {
    // Inherited from the legacy dropdown, where this queried the legacy
    // outline-button components. se-dropdown renders se-button and
    // se-split-button, which the old selectors never matched, so restoring
    // focus here has always been a no-op. Kept as a no-op for behavior
    // parity; wiring it to the se- components is a deliberate follow-up.
  }

  handlePanelKeystrokes(event: KeyboardEvent) {
    this.handleEscKeyDown(event);
    this.handleFocusChange();
  }

  handleIconTrigger(event: KeyboardEvent) {
    this.handleEnterKeyDown(event, true);
    this.handleEscKeyDown(event);
  }

  handleButtonTrigger(event: KeyboardEvent) {
    this.handleEnterKeyDown(event);
    this.handleEscKeyDown(event);
    this.handleFocusChange();
  }

  handleHoverStyles() {}

  /**
   * @todo: Ensure there IS a dropdown before triggering the functionality.
   * @returns {TemplateResult}
   */

  render(): TemplateResult {
    return html`
      <div
        id="dropdown-${this.seed}"
        class=${classMap({
          'dropdown': true,
          'dropdown--open': this.isOpen,
        })}
      >
        <div
          class="dropdown__trigger"
          @mouseenter="${!this.mobileControllerLegacy.isMobile ? this.show : null}"
          @mouseleave="${this.mobileControllerLegacy.isMobile
            ? null
            : this.handleFocusChange}"
        >
          ${this.buttonTemplate()} ${this.dropdownTemplate()}
        </div>
      </div>
    `;
  }

  /**
   * Template partial for the dropdown rendering.
   * @returns TemplateResult | null
   */
  dropdownTemplate(): TemplateResult | null {
    if (!this.hasDropdown) return null;

    return html`
      <div class="dropdown__panel" @keydown="${this.handlePanelKeystrokes}">
        ${this.headerTemplate()}
        <div class="control-spacer">
          <slot name="dropdown"></slot>
        </div>
        ${this.footerTemplate()}
      </div>
    `;
  }

  /**
   * Template partial for the "button" rendering.
   * @returns TemplateResult | null
   */
  buttonTemplate(): TemplateResult | null {
    return this.triggerUrl
      ? html`
          <se-split-button
            variant="${ifDefined(this.seTriggerVariant)}"
            button-target="${ifDefined(this.triggerTarget)}"
            button-url="${ifDefined(this.triggerUrl)}"
            button-label="${ifDefined(this.triggerLabel)}"
            @mouseenter="${this.handleHoverStyles}"
            @keydown="${this.handleButtonTrigger}"
            ?is-disabled=${this.isDisabled}
            controls="panel-${this.seed}"
            expanded="${this.isOpen ? 'true' : 'false'}"
          >
            <span
              class=${classMap({
                button__trigger: true,
                button__has_menu:
                  this.hasDropdown && this.seTriggerVariant == 'primary',
              })}
              >${this.triggerText}</span
            >
            ${this.iconWrapperTemplate()}
          </se-split-button>
        `
      : html`
          <se-button
            variant="${ifDefined(this.seTriggerVariant)}"
            button-target="${ifDefined(this.triggerTarget)}"
            button-url="${ifDefined(this.triggerUrl)}"
            button-label="${ifDefined(this.triggerLabel)}"
            @keydown="${this.handleButtonTrigger}"
            @click="${this.handleIconClick}"
            ?is-disabled=${this.isDisabled}
            controls="panel-${this.seed}"
            expanded="${this.isOpen ? 'true' : 'false'}"
          >
            <span class="button__trigger">${this.triggerText}</span>
            ${this.iconWrapperTemplate()}
          </se-button>
        `;
  }

  /**
   * Template partial for the icon rendering.
   * @todo: Add SR text for button.
   * @returns TemplateResult | null
   */
  iconWrapperTemplate(): TemplateResult | null {
    if (!this.hasDropdown) return null;

    return html`
      <span
        class=${classMap({
          icon__trigger: true,
          icon__bordered:
            this.triggerUrl && this.seTriggerVariant !== 'primary',
        })}
      >
        ${this.triggerUrl
          ? html` <se-button
              class="icon__button"
              variant="${ifDefined(this.seTriggerVariant)}"
              button-target="${ifDefined(this.triggerTarget)}"
              button-label="${ifDefined(this.triggerLabel)}"
              @keydown="${this.handleIconTrigger}"
              @click="${this.handleIconClick}"
              ?is-disabled=${this.isDisabled}
            >
              ${this.iconTemplate()}
              <span class="sr-only"
                >Sub-navigation for ${this.triggerText}</span
              >
            </se-button>`
          : html` ${this.iconTemplate()} `}
      </span>
    `;
  }

  /**
   * Template partial for the icon rendering.
   * @todo: Ensure chevron can be swapped out for other icons.
   * @todo: Ensure "standard" chevron-down can be replaced/rotated when menu is open.
   * @returns TemplateResult | null
   */
  iconTemplate(): TemplateResult | null {
    if (this.isOpen) {
      return html`
        <outline-icon
          slot="right"
          name="${'chevron-up'}"
          library="system"
          size="1em"
          color="brand-primary-2"
          label="${ifDefined(this.triggerUrl ? this.triggerLabel : undefined)}"
        ></outline-icon>
      `;
    } else {
      return html`
        <outline-icon
          slot="right"
          name="${'chevron-down'}"
          library="system"
          size="1em"
          color="brand-primary-2"
          label="${ifDefined(this.triggerUrl ? this.triggerLabel : undefined)}"
        ></outline-icon>
      `;
    }
  }

  /**
   * Template partial for the header rendering.
   * @returns TemplateResult | null
   */
  headerTemplate(): TemplateResult | null {
    if (!this.hasHeader) return null;
    return html`<slot name="header"></slot>`;
  }

  /**
   * Template partial for the footer rendering.
   * @returns TemplateResult | null
   */
  footerTemplate(): TemplateResult | null {
    if (!this.hasFooter) return null;
    return html`<slot name="footer"></slot>`;
  }

  handleIconClick(event: MouseEvent) {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    event.preventDefault();

    if (this.seTriggerVariant !== 'dropdown-nav') {
      return this.isOpen ? this.hide() : this.show();
    }

    if (header.searchOpen) {
      header.closeSearchPanel();
    }

    if (this.isOpen) {
      this.hide();
      return header.removeOverlay();
    } else {
      this.show();
      return header.applyOverlay();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-dropdown': SeDropdown;
  }
}
